
import { SoundType } from "../types";

/**
 * AUDIO ENGINE ROBUSTO PER IOS/ANDROID
 * Usa l'OscillatorNode per generare suoni sintetizzati.
 * Questo bypassa la necessità di caricare file MP3 e funziona su tutti i browser moderni
 * una volta "sbloccato" il contesto audio con un'interazione utente.
 */

let audioCtx: AudioContext | null = null;

const getAudioContext = () => {
    if (!audioCtx) {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
            audioCtx = new AudioContextClass();
        }
    }
    return audioCtx;
};

// Funzione da chiamare al primo click/touch dell'utente per sbloccare l'audio su iOS
export const unlockAudioContext = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
        ctx.resume().then(() => {
            console.log("Audio Context Resumed/Unlocked");
        });
        
        // Riproduce un suono inudibile per forzare lo sblocco
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 440;
        gain.gain.value = 0.0001; // Quasi silenzioso
        osc.start(0);
        osc.stop(0.1);
    }
};

let keepAliveOsc: OscillatorNode | null = null;
let keepAliveGain: GainNode | null = null;

/**
 * Attiva un micro-segnale audio impercettibile a frequenza inudibile (o guadagno quasi nullo).
 * Questo mantiene attivo il thread JavaScript di background su iOS/Android WebView
 * impedendo al sistema operativo di sospendere il GPS quando il rider va alla Home o apre il navigatore.
 */
export const startBackgroundKeepAlive = () => {
    try {
        const ctx = getAudioContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume();
        }
        if (keepAliveOsc) return; // Già attivo

        keepAliveOsc = ctx.createOscillator();
        keepAliveGain = ctx.createGain();

        keepAliveOsc.type = 'sine';
        keepAliveOsc.frequency.value = 30; // Frequenza sub-acustica impercettibile
        keepAliveGain.gain.value = 0.00001; // Guadagno virtualmente zero

        keepAliveOsc.connect(keepAliveGain);
        keepAliveGain.connect(ctx.destination);

        keepAliveOsc.start();
        console.log("Background keep-alive pulse active");
    } catch (e) {
        console.warn("Background keep-alive not supported or blocked", e);
    }
};

export const stopBackgroundKeepAlive = () => {
    try {
        if (keepAliveOsc) {
            keepAliveOsc.stop();
            keepAliveOsc.disconnect();
            keepAliveOsc = null;
        }
        if (keepAliveGain) {
            keepAliveGain.disconnect();
            keepAliveGain = null;
        }
        console.log("Background keep-alive pulse stopped");
    } catch (e) {
        console.warn("Error stopping keep-alive", e);
    }
};

const playTone = (freq: number, type: 'sine'|'square'|'triangle'|'sawtooth', duration: number, startTime: number, volume: number) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);

    gain.gain.setValueAtTime(volume, startTime);
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(startTime);
    osc.stop(startTime + duration);
};

export const playSynthesizedSound = (type: SoundType, volume: number = 0.5) => {
    const ctx = getAudioContext();
    if (!ctx) return;
    
    // Assicura che sia attivo (in caso di background wake)
    if (ctx.state === 'suspended') ctx.resume();

    const now = ctx.currentTime;
    const vol = Math.min(Math.max(volume, 0), 1); // Clamp 0-1

    switch (type) {
        case 'doorbell':
            // Ding Dong (Due toni armonici)
            playTone(660, 'sine', 1.0, now, vol);       // Ding (Mi)
            playTone(550, 'sine', 1.5, now + 0.6, vol); // Dong (Do diesis approx)
            break;

        case 'arcade':
            // Power Up (Slide veloce)
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'square';
            osc.frequency.setValueAtTime(220, now);
            osc.frequency.linearRampToValueAtTime(880, now + 0.3);
            gain.gain.setValueAtTime(vol * 0.3, now);
            gain.gain.linearRampToValueAtTime(0, now + 0.3);
            osc.start(now);
            osc.stop(now + 0.3);
            break;

        case 'classic':
            // Simple double beep
            playTone(880, 'sine', 0.1, now, vol);
            playTone(880, 'sine', 0.1, now + 0.15, vol);
            break;

        case 'elevator':
            // Soft pleasant chime
            playTone(523.25, 'triangle', 1.0, now, vol * 0.5); // C
            playTone(659.25, 'triangle', 1.0, now + 0.1, vol * 0.5); // E
            playTone(783.99, 'triangle', 2.0, now + 0.2, vol * 0.5); // G
            break;
            
        case 'success':
            // Positive confirmation
            playTone(440, 'sine', 0.1, now, vol);
            playTone(554, 'sine', 0.2, now + 0.1, vol);
            break;

        default:
            // Fallback Doorbell
            playTone(600, 'sine', 0.8, now, vol);
            playTone(480, 'sine', 1.2, now + 0.5, vol);
            break;
    }
};
